// services/modelChoice.js — the one saved model (founder, 2026-09-29).
//
// There were two pickers and each ignored the other: Account saved a model
// that only the Interview Coach read, and the chat kept its own choice in
// memory, never saved. Now there is one saved choice, and one place it is
// saved: Account → Assistant, into Firestore users/{uid}/profile/
// accountSettings.aiModel. The chat opens on it and may switch for one
// conversation; the coach reads it on the server (zagaRoutes.js, through
// modelForPlan).
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../components/FirebaseConfig';
import { DEFAULT_MODEL, STATIC_MENU } from './modelMenu';

/**
 * Whether a model can be the saved one. Gemini cannot: the Interview Coach
 * calls models through callAI (api/aiServices.js), which has no Google path,
 * so a saved Gemini ran GPT-4o mini there. The chat's own picker still offers
 * Gemini for one conversation, where LangChain runs it.
 */
export const savable = (id) => typeof id === 'string' && !id.startsWith('gemini-');

// What a saved value means, read the way the server reads it
// (services/modelPreference.js resolveModelPreference, BY_PREFERENCE), so
// this page, the chat and the coach name one model for one saved value
// (2026-09-29). A menu id is itself; an id an earlier Account page saved, and
// the two labels an earlier picker saved, run today's model of their family
// (chat-2); anything else — an old chat id such as 'claude-3-5-sonnet', which
// the server reads as "no choice made" — is no choice.
const SAVED_LABELS = {
  'claude-sonnet-5': 'claude-sonnet-5-5',
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
  'gpt-4o': 'gpt-6.1-sol',
  'sonnet (claude 3.5)': 'claude-sonnet-5-5',
  'flash (gpt-4o-mini)': 'claude-haiku-5-5',
};

/**
 * The model id a saved value stands for, or null when it names none. With
 * the server's menu, an id is itself when the menu lists it; without one,
 * when it is on today's menu (modelMenu.js STATIC_MENU).
 */
export function resolveSaved(saved, menu = null) {
  if (typeof saved !== 'string') return null;
  const known = (Array.isArray(menu) ? menu : STATIC_MENU).some(m => m?.id === saved);
  if (savable(saved) && known) return saved;
  return SAVED_LABELS[saved.trim().toLowerCase()] || null;
}

/** Whether the server's menu lets this person run `id` here: listed, allowed, and available. */
export function canUseModel(menu, id) {
  if (!Array.isArray(menu) || typeof id !== 'string') return false;
  const m = menu.find(x => x?.id === id);
  return Boolean(m && m.allowed === true && m.available !== false);
}

/** Fired on window when Account saves a new choice, so an open chat follows it. */
export const MODEL_SAVED = 'aa:model-saved';

const settingsDoc = (uid) => doc(firestore, 'users', uid, 'profile', 'accountSettings');

/** The saved model id, or null when nothing is saved or it cannot be read. */
export async function readSavedModel(uid) {
  if (!uid || !firestore) return null;
  try {
    const snap = await getDoc(settingsDoc(uid));
    const id = snap?.exists?.() ? snap.data()?.aiModel : null;
    return typeof id === 'string' && id ? id : null;
  } catch (err) {
    console.error('[model] could not read the saved model', err);
    return null;
  }
}

/**
 * Saves the choice and tells an open chat. setDoc with merge: the document
 * does not exist for anyone who has never saved a setting, and updateDoc
 * fails on a missing document instead of creating it. Throws on failure, so
 * the page can say it was not saved.
 */
export async function saveModel(uid, id) {
  await setDoc(settingsDoc(uid), { aiModel: id }, { merge: true });
  try { window.dispatchEvent(new CustomEvent(MODEL_SAVED, { detail: { id } })); } catch { /* no window */ }
}

/**
 * The model a chat opens on (chat-2, 2026-10-30): Auto, unless this plan may
 * choose and the person saved a model the server lists, allows and can run.
 *
 * The saved value comes first because Account can save while the chat is
 * open (MODEL_SAVED), after the server last answered. A value saved before
 * chat-2 ('gpt-4o', 'claude-sonnet-5') is not on today's menu; the server
 * maps it forward and says so as `chatModel`, and the chat opens on that.
 *
 * Only on the server's word. Before /api/plans/usage answers, or when it
 * fails, there is no menu to check against, and the chat opens on Auto.
 */
export function openingModel(saved, usage) {
  if (!usage || !Array.isArray(usage.menu)) return DEFAULT_MODEL;
  if (usage.canChooseModel === false) return DEFAULT_MODEL;
  const own = resolveSaved(saved, usage.menu);
  if (own && canUseModel(usage.menu, own)) return own;
  if (canUseModel(usage.menu, usage.chatModel)) return usage.chatModel;
  return DEFAULT_MODEL;
}
